import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { CompanyType, UserInfo } from '../data/type'
import { APIServer } from '../data/api'
import { checkAppNetwork } from '../util'
import { App } from '..'
import { AppAuth } from '../auth'

export const useUserStore = defineStore('user-status', () => {
  const userInfo = ref<UserInfo | null>(null)

  /**
   * 사용자의 회사 ID
   */
  const userCompanyId = computed<number | null>(() => {
    if (!userInfo.value) return null
    return userInfo.value.company_id
  })

  /**
   * 관리자 권한 여부
   */
  const isAdmin = computed(() => userInfo.value?.isAdmin ?? false)

  /**
   * 품질점검 회사 소속 여부
   */
  const isQualityCompany = computed(() => userInfo.value?.company?.category === 'QUALITY')

  /**
   * 품질점검 총괄 여부 (QUALITY 회사 + MANAGER 역할)
   */
  const isQualityManager = computed(
    () => isQualityCompany.value && userInfo.value?.role === 'MANAGER',
  )

  //오프라인 대응을 위해 localStorage에도 저장
  async function initUserInfo(): Promise<void> {
    const result: UserInfo = await APIServer.getMe()

    // 서버에서 isAdmin을 포함하지 않은 경우 false로 처리
    if (result.isAdmin === undefined) {
      result.isAdmin = false
    }

    localStorage.setItem('USER_INFO', JSON.stringify(result))
    userInfo.value = result
  }

  function checkEnterprise(showToast: boolean = false, reverse: boolean = false): boolean {
    //reverse 는 기본값을 컨트롤 하기 위함(UI 로딩용)
    if (!userInfo.value) return reverse ? true : false
    const isEnterprise = userInfo.value.company.type === CompanyType.BIZ
    if (showToast && !isEnterprise) {
      App.toastInstance.add('error', '기업고객 전용 기능입니다.')
    }
    return isEnterprise
  }

  async function getUserInfoFromLocalStorage(): Promise<UserInfo | null> {
    const localUserInfo = localStorage.getItem('USER_INFO')
    if (localUserInfo) {
      userInfo.value = JSON.parse(localUserInfo)
    } else {
      return null
    }

    return userInfo.value
  }
  async function getMe(isConnected?: boolean): Promise<UserInfo | null> {
    const connected = isConnected ?? (await checkAppNetwork())
    if (connected) {
      await initUserInfo()
    } else {
      await getUserInfoFromLocalStorage()
    }
    return userInfo.value
  }

  async function withdrawUser(): Promise<void> {
    if (!userInfo.value) {
      throw new Error('사용자 정보를 찾을 수 없습니다.')
    }

    try {
      await AppAuth.withdrawUser()
    } catch (error) {
      console.error('회원 탈퇴 중 오류:', error)
      App.toastInstance.add('error', (error as Error).message)
    }
  }

  async function deleteUserData(): Promise<void> {
    if (userInfo.value) {
      userInfo.value = null
      localStorage.removeItem('USER_INFO')
    }
  }
  return {
    userInfo,
    userCompanyId,
    isAdmin,
    isQualityCompany,
    isQualityManager,
    initUserInfo,
    getMe,
    getUserInfoFromLocalStorage,
    checkEnterprise,
    withdrawUser,
    deleteUserData,
  }
})
